/**
 * How a staged row reads on screen, shared by the preview and by the popup the category mapper
 * opens onto the rows behind a rule.
 *
 * It is one module because the two show the same rows and must say the same thing about them: a row
 * described as "Ready" in one place and "Bank record only" in the other is a person mapping against
 * a screen that disagrees with itself.
 *
 * Keep the wording in step with Import_Preview_Service's status constants.
 */
export const STATUS_LABELS = {
	ready: 'Ready',
	splits_existing: 'Splits an imported transaction',
	uncategorized: 'Uncategorized',
	raw_only: 'Bank record only',
	skipped: 'Skipped',
	duplicate: 'Already imported',
	needs_account: 'Needs an account',
	needs_category: 'Needs a category',
	waiting_on_split: 'Waiting on its order',
};

export const STATUS_EXPLANATIONS = {
	ready: 'Will become a bank record and a transaction in the budget.',
	uncategorized: 'Will become a bank record and a transaction with no category, to categorize in the budget.',
	splits_existing: 'Its order was imported whole before Monarch split it. The transaction it became takes this item as one of its splits.',
	raw_only: 'Will become a bank record but no transaction — transfers and card payments are real bank activity, not spending.',
	skipped: 'Excluded on purpose by a mapping.',
	duplicate: 'Already in this budget under the same id, so it will be left alone.',
	needs_account: 'Blocked until the account it came from is mapped.',
	needs_category: 'Blocked until its category is mapped.',
	waiting_on_split: 'One item of a split order. The order is one charge and imports whole, so this waits until every item of it is mapped.',
};

export function statusLabel(status) {
	return STATUS_LABELS[status] || status;
}

/** A staged row's date, which the API sends as a unix timestamp. */
export function formatRowDate(timestamp) {
	if(!timestamp) {
		return '';
	}
	return new Date(timestamp * 1000).toISOString().slice(0, 10);
}

/**
 * Rows a commit would write, from a summary's counts. Everything else is skipped, blocked or already
 * in. An uncategorized row is written like a ready one, just with no category on its transaction.
 */
export function writableCount(counts) {
	counts = counts || {};
	return (counts.ready || 0) + (counts.uncategorized || 0) + (counts.raw_only || 0) + (counts.splits_existing || 0);
}

/** Rows still waiting on a mapping, from a summary's counts -- what keeps a file from being finished. */
export function blockedCount(counts) {
	counts = counts || {};
	return (counts.needs_account || 0) + (counts.needs_category || 0) + (counts.waiting_on_split || 0);
}

/**
 * How a commit's split orders reconcile the rows the preview counted with the transactions written,
 * or null when there is nothing to reconcile. Each order's items are counted as rows but land as one
 * transaction, so without this "Import 9 rows" answered by "Imported 6 transactions" reads as three
 * rows lost.
 */
export function combinedOrdersNote(committed) {
	const orders = (committed && committed.orders_written) || 0;
	const rows = (committed && committed.order_rows_written) || 0;
	if(rows <= orders) {
		return null;
	}
	return `with ${rows} rows combined into ${orders} split ${orders === 1 ? 'order' : 'orders'}`;
}

const AMAZON_ORDER = /orderID=(\d{3}-\d{7}-\d{7})/;

/**
 * A staged row's notes, taken apart for display: the lines of text, and the Amazon order they name.
 *
 * Monarch's Amazon extension writes one line per item, then the order's link. The link is pulled out
 * only when it carries a whole order id; anything else stays a line of text, which is how a note cut
 * short by the old 255-character column shows that it was cut rather than linking somewhere broken.
 */
export function rowNoteParts(notes) {
	const lines = [];
	let order = null;
	for(const line of (notes || '').split('\n')) {
		const text = line.trim();
		if(!text) {
			continue;
		}
		const match = !order && /^https?:\/\//.test(text) && text.match(AMAZON_ORDER);
		if(match) {
			order = { id: match[1], url: text };
		} else {
			lines.push(text);
		}
	}
	return { lines, order };
}
